import { pageFaqAdditions } from "@/data/pageFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { m, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Calendar,
  ChevronRight,
  ClipboardCheck,
  Layers,
  Repeat,
  Shield,
} from "lucide-react";
import { BookFitCall } from "@/components/site/BookFitCall";
import { canonicalLinks, jsonLd, ogUrl, ogImage, breadcrumbSchema, webPageSchema } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";
import { DECISION_SPRINT_LIVE } from "@/lib/flags";
import { useTrackView } from "@/lib/useTrackView";
import { SCAN_QUESTION_COUNT, SCAN_DURATION_MINUTES } from "@/lib/diagnostic";
import { formatPrice, findRow } from "@/data/pricing";
import {
  CREDIT_RULE,
  NINETY_DAY_INSTALL_TERMS,
  NINETY_DAY_STANDARD,
  RENEW_OR_EXIT,
  TERM_RULE,
} from "@/data/terms";
import { offerCatalogSchema, tiers } from "@/data/tiers";
import { EngagementLadder } from "@/components/diagrams/EngagementLadder";
import { DiagramFrame, rise } from "@/components/diagrams/DiagramFrame";
import { PhotoBand } from "@/components/site/PhotoBand";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SampleScorecard } from "@/components/samples/SampleScorecard";
import { SampleAccountabilityChart } from "@/components/samples/SampleAccountabilityChart";
import { SampleRoadmap } from "@/components/samples/SampleRoadmap";
import leadershipTableImg from "@/assets/photos/leadership-table.webp";

const priceOf = (name: string) => formatPrice(findRow(name));

const cardPrice = (rowName: string) => formatPrice(findRow(rowName));

const PRICING_BASIS =
  "Pricing is based on the size of the business and on how much of the work is on site versus virtual. Every figure is a from price. Anything running a quarter or longer is charged as a monthly fee. Anything shorter is a fixed price agreed up front. All figures are quoted plus GST.";

const diagnosisCards = [
  {
    name: "Operating Scan",
    price: cardPrice("Operating Scan"),
    bullets: [
      `${SCAN_QUESTION_COUNT} plain statements, answered from Not us to Always`,
      "Instant score with the weakest areas named",
      "Conversation afterwards only if you want one",
    ],
    details: (
      <ul className="list-disc space-y-1 pl-4">
        <li>About {SCAN_DURATION_MINUTES} minutes</li>
        <li>Done online, no scheduling</li>
      </ul>
    ),
    href: "/scan",
    cta: "Take the scan",
  },
  {
    name: "Operating Diagnostic",
    price: cardPrice("Operating Diagnostic"),
    bullets: [
      "Team Survey across your staff (30 statements)",
      "Ajility's read of the numbers you supply",
      "A 90-minute working session with you",
    ],
    details: (
      <ul className="list-disc space-y-1 pl-4">
        <li>Desktop diagnostic, run remotely</li>
        <li>Two to three weeks</li>
        <li>Anything beyond the scope is a new quote</li>
      </ul>
    ),
    href: "/contact",
    cta: "Book the diagnostic",
  },
  {
    name: "Full Diagnostic and 90-Day Roadmap",
    price: cardPrice("Full Diagnostic and 90-Day Roadmap"),
    bullets: [
      "On site: Ajility walks the floor and reads the data and reports",
      "Interviews with staff and leaders",
      "A prioritised 90-day roadmap with owners and dates",
    ],
    details: (
      <ul className="list-disc space-y-1 pl-4">
        <li>One entity, one site, up to six interviews, with site / office visits</li>
        <li>Delivered over three to four weeks</li>
        <li>Multi-site and group businesses are priced on scope</li>
        <li>Anything beyond the scope is a new quote</li>
      </ul>
    ),
    href: "/contact",
    cta: "Book the full diagnostic",
  },
];

/** Each one-off link names its destination rather than a generic label. */
const oneOffCta = (name: string) =>
  name === "Service installs" ? "See the service installs" : name.startsWith("Remuneration") ? `See ${name}` : `See the ${name.split(",")[0]}`;

const oneOffCards = [
  ...tiers.find((tier) => tier.id === "one-offs")?.options.filter((option) => ["partner-alignment-review", "structure-and-performance-reset"].includes(option.id)).map((option) => ({ name: option.name, price: formatPrice(option.price), bullets: option.youGet, href: option.path })) ?? [],
  {
    name: "90-Day Execution Sprint",
    price: cardPrice("90-Day Execution Sprint"),
    bullets: [
      "90-day execution sprints",
      "Top 3 to 5 company priorities with assigned owners",
      "Weekly tracking of priority progress",
    ],
    href: "/services/strategy-execution",
  },
  {
    name: "Service installs",
    price: cardPrice("One process, one fix"),
    bullets: [
      "Meeting cadence, operating system and scorecards",
      "Org design, team structure and accountability",
      "Systems improvement and process rebuilds",
    ],
    href: "/services",
  },
  {
    name: "Rolling Cash Forecast, 13 weeks",
    price: cardPrice("Rolling Cash Forecast, 13 weeks"),
    bullets: [
      "13-week rolling cash forecast built on your actuals",
      "Operating cadence to keep it current",
      "The most-requested finance artefact in a founder-led business",
    ],
    href: "/services/performance-discipline",
  },
  {
    name: "Remuneration and Incentive Design",
    price: cardPrice("Remuneration and Incentive Design"),
    bullets: [
      "Bonus and commission structures that pay for the behaviour you want",
      "Modelled against your P&L before anyone is told",
      "Clear roles, targets and quarterly objectives",
    ],
    href: "/services/team-structure",
  },
  {
    name: "Annual Operating Budget",
    price: cardPrice("Annual Operating Budget"),
    bullets: [
      "Budget built from operating drivers",
      "Tied to the plan and the scorecard that tracks it",
      "Not last year plus a percentage",
    ],
    href: "/services/strategy-execution",
  },
  {
    name: "Pricing and Margin Review",
    price: cardPrice("Pricing and Margin Review"),
    bullets: [
      "Review price architecture, margin waterfall and unit economics",
      "Costed set of price moves",
      "See where money is being left on the table",
    ],
    href: "/services/performance-discipline",
  },
];

const retainerCards = [
  {
    name: "Fortnightly advisory rhythm",
    price: cardPrice("Fortnightly rhythm base"),
    bullets: [
      "Fortnightly leadership cadence and numbers review",
      "Quarterly plan reset with the team",
      "Suits most founder-led businesses",
    ],
  },
  {
    name: "Weekly advisory rhythm",
    price: cardPrice("Weekly rhythm base"),
    bullets: [
      "Weekly check-in for fast-moving businesses",
      "Same scorecard, planning and accountability rhythm",
      "Suits businesses where a fortnight is too long between checks",
    ],
  },
  {
    name: "Weekly advisory rhythm, above $15M",
    price: cardPrice("Weekly rhythm base, businesses above $15M"),
    bullets: [
      "The weekly rhythm at the scale of a larger operation",
      "More leaders in the cadence and more numbers to hold",
      "A size signal, not a separate product",
    ],
  },
];

const decisionSprintCards = [
  {
    name: "Rapid Decision Review",
    price: cardPrice("Rapid Decision Review"),
    bullets: [
      "3 business days",
      "One bounded decision, documents supplied",
      "Two to four options scored, four to six page memo",
    ],
  },
  {
    name: "Decision Sprint",
    price: cardPrice("Decision Sprint"),
    bullets: [
      "9 business days",
      "Up to four stakeholder interviews",
      "Three to five options scored, 90-minute decision session",
    ],
  },
  {
    name: "Deep Decision Case",
    price: cardPrice("Deep Decision Case"),
    bullets: [
      "3 weeks",
      "Six to eight interviews, scenarios and sensitivity",
      "Two-hour decision session and a 30-day launch plan",
    ],
  },
];

const faqs = [
  {
    q: "How is fractional COO pricing structured?",
    a: PRICING_BASIS,
  },
  {
    q: "What is the cheapest way to start?",
    a: "The Operating Scan and the 30-minute fit call are free. The first paid step is the Operating Diagnostic.",
  },
  {
    q: "What does a fractional COO retainer cost?",
    a: `Retainers start with a rhythm base (fortnightly or weekly) and add the service lines you want actively led. Most composed retainers for businesses under $15M start ${priceOf("Fortnightly rhythm base")}, depending on the mix and the size of the business. Businesses above $15M should expect the weekly rhythm to scope ${priceOf("Weekly rhythm base, businesses above $15M")}.`,
  },
  {
    q: "Can I spread payment on an install?",
    a: "Yes. An install that runs a quarter or longer is charged as a monthly fee across the install period rather than as a single fixed price.",
  },
  {
    q: "What is the 90-Day Operating System Install?",
    a: `The Full Diagnostic and 90-Day Roadmap followed straight away by the Operating System & Governance install, as one engagement, ${priceOf("90-Day Operating System Install")}. Thirteen weeks. ${NINETY_DAY_INSTALL_TERMS}. It suits owners who already know the business needs an operating system and want one fee, one scope and a hard stop.`,
  },
  {
    q: "Is the 90-Day Operating System Install a retainer?",
    a: "No. It ends at Week 13, with a review session six weeks after handover. A retainer is a different purchase: Ajility stays in the leadership rhythm month to month, committed one quarter at a time. Some businesses move from the install into a retainer. Many do not, and the install is built so that they do not need to.",
  },
  {
    q: "Do I have to commit long term?",
    a: `${TERM_RULE}. There are no long-term lock-ins.`,
  },
  {
    q: "Which fractional COO engagement should I start with?",
    a: "Almost everyone starts with the Operating Diagnostic. It tells both of us exactly what to install, in what order, and whether a single install or a retainer is the right fit.",
  },
  {
    q: "Who will I actually be working with?",
    a: "Ajility is a single-principal practice, so you work with Fractional COO Adam Ayoub directly. Ajility is not a firm that sells you a partner and sends juniors. The person you meet on the first call is the person in your leadership meetings.",
  },
  {
    q: "Do you work outside Sydney, Melbourne and Perth?",
    a: "Yes. Ajility works on site across Sydney, Melbourne, the Central Coast, Newcastle and Perth, and remotely with founder-led businesses across Australia.",
  },
  ...pageFaqAdditions["/packages"],
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://ajility.com.au/packages#faq",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const metaDescription = "Ajility fractional COO pricing for diagnosis, one-offs and retainers. All figures are from prices, plus GST.";

export const Route = createFileRoute("/packages")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Packages & Pricing: Service-Based COO Engagements | Ajility" },
      { name: "description", content: metaDescription },
      { property: "og:title", content: "Packages & Pricing: Service-Based COO Engagements | Ajility" },
      { property: "og:description", content: metaDescription },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Packages & Pricing: Service-Based COO Engagements | Ajility" },
      { name: "twitter:description", content: metaDescription },
      ...ogImage("packages", "Ajility fractional COO programs and pricing"),
      ogUrl("/packages"),
    ],
    links: canonicalLinks("/packages"),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Packages", path: "/packages" }])),
      jsonLd(webPageSchema({ image: "packages", path: "/packages", name: "Packages & Pricing: Service-Based COO Engagements | Ajility", description: metaDescription })),
      jsonLd(faqSchema),
      jsonLd(offerCatalogSchema()),
    ],
  }),
  component: PackagesPage,
});

const qualification = [
  {
    title: "Minimum engagement term: one quarter",
    desc: `${TERM_RULE}.`,
  },
  {
    title: "A dedicated point person",
    desc: "One team member owns the engagement internally and keeps the business side moving.",
  },
  {
    title: "Weekly check-ins",
    desc: "Leadership shows up to the weekly rhythm. Consistency is what makes the system take hold.",
  },
  {
    title: "Quarterly business reviews",
    desc: "Progress is reviewed openly against the plan every quarter.",
  },
  {
    title: "Metrics on the table",
    desc: "Performance data is shared and tracked. Decisions get made on facts.",
  },
  {
    title: "Appetite to improve",
    desc: "You want the business to run differently, not just hear about how it could.",
  },
];

function TierLadder({ className }: { className?: string }) {
  const reduced = useReducedMotion() ?? false;
  const tiers = [
    { name: "Diagnosis", line: "Find out what is actually wrong" },
    { name: "One-offs", line: "Fix one thing properly" },
    { name: "Retainers", line: "Run it with me monthly" },
  ];

  return (
    <DiagramFrame className={className} scrollable={false} caption="Three tiers, one ladder. Every figure on this page is a from price, plus GST." captionClassName="italic">
      <div className="mx-auto max-w-5xl">
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)] items-stretch gap-1 sm:gap-3">
          {tiers.flatMap((t, i) => [
            <m.div key={t.name} {...rise(reduced, i * 0.07)} className={`flex min-w-0 flex-col items-center justify-center rounded-lg border px-2 py-5 text-center sm:px-4 sm:py-8 ${i === 2 ? "border-midnight bg-midnight text-frost" : "border-border bg-card text-foreground"}`}>
              <h2 className="font-serif text-lg sm:text-2xl">{t.name}</h2>
              <p className={`mt-2 text-xs leading-relaxed sm:text-sm ${i === 2 ? "text-steel-on-dark" : "text-charcoal"}`}>{t.line}</p>
            </m.div>,
            ...(i < 2 ? [<ArrowRight key={`${t.name}-arrow`} className="h-4 w-4 shrink-0 self-center text-steel sm:h-5 sm:w-5" aria-hidden="true" />] : []),
          ])}
        </div>
        <p className="mt-5 text-center text-sm text-charcoal">credits apply on the entry path only, within 30 days</p>
      </div>
    </DiagramFrame>
  );
}

function SimpleCard({
  name,
  price,
  bullets,
  details,
  href,
  cta,
  rung,
}: {
  name: string;
  price: string | null;
  bullets: string[];
  details?: React.ReactNode;
  href?: string;
  cta?: string;
  rung?: string;
}) {
  const viewRef = useTrackView<HTMLDivElement>(
    "packages_view",
    { rung: rung ?? name },
    rung ?? "",
  );
  return (
    <div ref={viewRef} className="card-panel card-interactive flex flex-col">
      <div className="min-h-[4rem]">
        <h4 className="font-serif text-lg leading-snug text-midnight-deep line-clamp-2">
          {name}
        </h4>
        {price ? (
          <p className="mt-1 text-sm font-medium text-midnight-deep">{price}</p>
        ) : null}
      </div>
      <ul className="mt-4 flex-1 space-y-2 text-sm text-foreground/80">
        {bullets.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ul>
      <div className="mt-auto pt-4">
        {details ? (
          <details className="group mb-3 rounded-lg border border-border bg-mist/60">
            <summary className="flex min-h-10 cursor-pointer list-none items-center justify-between px-3 py-2 text-xs font-medium [&::-webkit-details-marker]:hidden">
              <span>Details and inclusions</span>
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-steel transition group-open:rotate-90" />
            </summary>
            <div className="px-3 pb-3 text-sm text-foreground/70">{details}</div>
          </details>
        ) : null}
        {href ? (
          <Link
            to={href}
            className="inline-flex items-center gap-1 text-sm font-medium text-midnight hover:underline"
          >
            {cta} <ArrowRight className="h-4 w-4" />
          </Link>
        ) : null}
      </div>
    </div>
  );
}

const ninetyDayPhases = [
  {
    weeks: "Phase 1. Weeks 1 to 3.",
    title: "Diagnose.",
    end: "You can see the business on paper and you know what to fix first.",
    does: "Kickoff with the leadership team; up to six interviews, founder first and longest; a walk of the floor and at least one meeting observed without intervening; a read of the P&L, margin, concentration and cash; the Team and Leadership Surveys across the team; an Operating Maturity baseline taken before anything is built.",
    delivered: "The Current State Analysis Report, the confidential leadership read, five to ten quick wins you can act on without Ajility, and the 90-day plan.",
    gate: "The report is walked through with you one to one. Continue, or stop here with the plan in hand.",
  },
  {
    weeks: "Phase 2. Weeks 4 to 6.",
    title: "Decide.",
    end: "The leadership team can state the one-year plan, the quarter's theme and its Critical Number without looking.",
    does: "Three facilitated sessions with the leadership team, not the founder alone; the accountability chart, seat by seat, with empty seats named rather than filled; decision rights written down, so the decisions that currently reach you stop reaching you.",
    delivered: "The strategy layer, the accountability chart and the decision rights matrix.",
    gate: "Each leader states the Critical Number unaided. If they cannot agree it, Ajility stops and sorts that out before building anything on top of it.",
  },
  {
    weeks: "Phase 3. Weeks 7 to 9.",
    title: "Build.",
    end: "The operating system exists in your tool and your people can find their work in it.",
    does: "Builds the structure, the fields, the statuses and the views; moves existing work across and enriches it rather than dumping it; sets the quarter's three Rocks with owners and done definitions; builds the scorecard against a data-quality gate, six to nine measures, no measure without a source; puts the operating rhythm in calendars for six months.",
    delivered: "The system, live, with your work in it, the scorecard, the Rocks and the calendar.",
    gate: "The person who will own it on your side navigates it without help and finds three specific pieces of work unaided.",
  },
  {
    weeks: "Phase 4. Weeks 10 to 13.",
    title: "Run and hand over.",
    end: "The business runs the rhythm without Ajility in the room.",
    does: "Chairs the first leadership meeting, co-chairs the second, observes the rest and debriefs after each; watches whether actuals are entered before the meeting, whether Rocks move, and whether operational work is crowding out the strategic; re-scores Operating Maturity against the baseline; writes the playbook for how the system runs.",
    delivered: "The dashboard, the playbook, the maturity re-score with the honest reading of what did not move, and the handover pack.",
    gate: "Your chair has run two consecutive leadership meetings unaided and to time, and the scorecard carries at least three weeks of real actuals. Then a review session six weeks after handover, included.",
  },
];

function NinetyDaySection() {
  const viewRef = useTrackView<HTMLDivElement>(
    "packages_view",
    { rung: "90-Day Operating System Install" },
    "90-Day Operating System Install",
  );

  return (
    <div id="ninety-day" className="mb-16 scroll-mt-28">
      <h3 className="mb-2 font-serif text-2xl sm:text-3xl">Or do the whole thing in 90 days</h3>
      <p className="mb-8 max-w-3xl text-sm text-foreground/80">
        Most owners take the ladder one rung at a time. Some already know the business needs an operating system and want it done in one go, with one fee, one scope and a hard stop. The 90-Day Operating System Install is the Full Diagnostic and 90-Day Roadmap followed straight away by the Operating System & Governance install, with the plan executed inside the same 90 days.
      </p>

      <div ref={viewRef} className="card-panel grid gap-10 lg:grid-cols-2">
        <div>
          <h4 className="font-serif text-2xl text-midnight-deep">90-Day Operating System Install</h4>
          <p className="mt-2 text-lg font-medium text-midnight-deep">{cardPrice("90-Day Operating System Install")}</p>
          <p className="mt-4 text-sm text-foreground/80">13 weeks, fixed. One entity, one site, up to six interviews, with site / office visits. Larger scope is quoted.</p>
          <p className="mt-4 text-sm text-foreground/80">{NINETY_DAY_INSTALL_TERMS}.</p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              onClick={() => trackEvent("cta_click", { placement: "ninety_day_card" })}
              className="inline-flex items-center gap-2 rounded-full bg-midnight px-5 py-3 text-sm font-medium text-frost transition hover:bg-midnight-deep"
            >
              Book the 90-day install <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <a href="#week-by-week" className="text-sm font-medium text-midnight underline decoration-border underline-offset-4 hover:decoration-midnight">See the week-by-week below</a>
          </div>
        </div>
        <div>
          <h4 className="font-serif text-xl text-midnight-deep">What you walk away with</h4>
          <ul className="mt-4 space-y-2 text-sm text-foreground/80">
            <li>The Current State Analysis Report and a confidential leadership read</li>
            <li>A 90-day plan with a named owner, a date and a done measure on every priority</li>
            <li>The one-year plan, the quarter&apos;s theme and its Critical Number, agreed by the leadership team</li>
            <li>An accountability chart with every seat, every gap and who decides what without you</li>
            <li>The operating system built in your tool, with existing work migrated into it</li>
            <li>A weekly scorecard of six to nine measures with at least three weeks of real actuals</li>
            <li>The leadership meeting running to time, chaired by your people, not by Ajility</li>
            <li>A written playbook for how the business runs itself, and a review six weeks after handover</li>
          </ul>
        </div>
      </div>
      <p className="mt-4 text-sm text-muted-foreground">Already done the Full Diagnostic with Ajility? Sign up to the install within 30 days of your report and you pay the remainder.</p>

      <div id="week-by-week" className="scroll-mt-28 pt-12">
        <h4 className="font-serif text-2xl sm:text-3xl">Week by week, gate by gate</h4>
        <div className="mt-6 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {ninetyDayPhases.map((phase) => (
            <article key={phase.weeks} className="rounded-2xl border border-border bg-card p-6">
              <span className="eyebrow">{phase.weeks}</span>
              <h5 className="mt-3 font-serif text-xl">{phase.title}</h5>
              <p className="mt-3 text-sm text-foreground/80"><span className="font-semibold text-foreground">By the end of it: </span>{phase.end}</p>
              <dl className="mt-5 space-y-4 text-sm text-foreground/80">
                <div><dt className="font-semibold text-foreground">Ajility does:</dt><dd className="mt-1">{phase.does}</dd></div>
                <div><dt className="font-semibold text-foreground">Delivered:</dt><dd className="mt-1">{phase.delivered}</dd></div>
              </dl>
              <p className="mt-5 border-t border-border pt-4 text-sm font-bold"><span>Gate: </span>{phase.gate}</p>
            </article>
          ))}
        </div>
      </div>

      <div className="pt-12">
        <h4 className="font-serif text-2xl sm:text-3xl">What the deliverables look like</h4>
        <p className="mt-3 max-w-4xl text-sm text-muted-foreground">Three of the documents every 90-Day Operating System Install produces, filled in for a fictional scaffolding contractor so you can see the shape. Harbourline Scaffolding is illustrative and not a real client. Yours carries your seats, your measures and your thresholds.</p>
        <Tabs defaultValue="scorecard" className="mt-6 min-w-0 max-w-full">
          <div className="scroll-table overflow-x-auto pb-2">
            <TabsList className="h-auto min-w-max">
              <TabsTrigger value="scorecard">Weekly Scorecard</TabsTrigger>
              <TabsTrigger value="accountability">Accountability chart</TabsTrigger>
              <TabsTrigger value="roadmap">90-Day Roadmap</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="scorecard" className="min-w-0 max-w-full overflow-hidden"><SampleScorecard /></TabsContent>
          <TabsContent value="accountability" className="min-w-0 max-w-full overflow-hidden"><SampleAccountabilityChart /></TabsContent>
          <TabsContent value="roadmap" className="min-w-0 max-w-full overflow-hidden"><SampleRoadmap /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function PackagesPage() {
  return (
    <>
      {/* HERO */}
      <section className="relative gradient-navy pb-24 pt-36 text-frost sm:pb-32 sm:pt-44">
        <div className="grid-lines absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
          <span className="eyebrow on-dark mb-6 inline-block">Fractional COO packages and pricing</span>
          <h1 className="font-serif text-4xl leading-[1.08] sm:text-5xl lg:text-6xl">
             Fractional COO pricing for defined operating outcomes.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-frost/80">
              {`Where it is not yet clear that a system is the constraint, start with the Operating Diagnostic ${priceOf("Operating Diagnostic")}, which tests how work and data actually flow before recommending whether to keep, reconfigure or replace.`}{" "}
             See the work, the handover and the price before you decide. Start with a diagnosis, install what the business is missing and use a retainer only when several service lines need ongoing leadership. If you already know the decision you need to make, the diagnostic tells you whether you are right and gives you something to show the team.
          </p>
          <p className="mt-4 text-sm text-frost/80">All prices plus GST. Installs can be paid monthly over the install period.</p>
        </div>
      </section>

      {/* TIER LADDER */}
      <section className="bg-background py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <TierLadder />
        </div>
      </section>

      <PhotoBand
        src={leadershipTableImg}
        alt="A leadership team around a table reviewing operating plans and scorecards"
        surface="background"
      />

      {/* HOW IT WORKS */}
      <section className="bg-midnight-deep py-20 text-frost">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <h2 className="mb-4 font-serif text-3xl sm:text-4xl">The fractional COO engagement ladder</h2>
          <p className="mb-10 max-w-3xl text-frost/75">Start by finding the operating problem, fix the right thing and add ongoing leadership only when the business needs it.</p>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: ClipboardCheck, step: "1", t: "Start free", d: "Take the free Operating Scan, or book a free 30-minute fit call." },
              { icon: Shield, step: "2", t: "Diagnose", d: "The Operating Diagnostic maps exactly what to fix, in what order." },
              { icon: Layers, step: "3", t: "Install", d: "Install only what the roadmap calls for, one service line at a time, or take the diagnosis and the operating system install together as the 90-Day Operating System Install." },
              { icon: Repeat, step: "4", t: "Retain", d: "Need several service lines run ongoing? A retainer is scoped around that mix." },
            ].map((s) => (
              <div key={s.step} className="relative rounded-2xl border border-white/10 bg-white/5 p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-steel/20 text-frost">
                  <s.icon className="h-6 w-6" />
                </div>
                <span className="eyebrow on-dark mb-2 block">Step {s.step}</span>
                <h3 className="font-serif text-xl">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-frost/70">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHARGE MODEL */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <span className="eyebrow mb-3 block text-steel">How we charge</span>
          <h2 className="font-serif text-3xl sm:text-4xl">The charge model, in plain language</h2>
          <p className="mt-4 text-foreground/80">{PRICING_BASIS}</p>
        </div>
      </section>

      {/* THE THREE TIERS */}
      <section className="bg-mist py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="mb-12 text-center">
            <span className="eyebrow mb-3 block text-steel">What you choose from</span>
             <h2 className="font-serif text-3xl sm:text-4xl">Diagnosis, one-off installs and fractional COO retainers</h2>
            <p className="mx-auto mt-4 max-w-3xl text-foreground/70">
              Start by finding out what&apos;s wrong. Fix one thing, or have it run with you.
            </p>
          </div>

          <div className="mb-16">
            <EngagementLadder />
          </div>

          {/* DIAGNOSIS */}
          <div className="mb-16">
            <h3 className="mb-8 font-serif text-2xl sm:text-3xl">Diagnosis</h3>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {diagnosisCards.map((c) => (
                <SimpleCard key={c.name} {...c} rung={c.name} />
              ))}
            </div>
          </div>

          <NinetyDaySection />

          {/* DECISION SPRINT */}
          {DECISION_SPRINT_LIVE ? (
            <div className="mb-16">
              <h3 className="mb-2 font-serif text-2xl sm:text-3xl">Or decide one thing</h3>
              <p className="mb-8 max-w-3xl text-sm text-foreground/80">
                The ladder diagnoses the business. The{" "}
                <Link to="/decision-sprint" className="font-medium text-midnight underline decoration-border underline-offset-4 transition hover:decoration-midnight">
                  Decision Sprint
                </Link>{" "}
                diagnoses one decision: one high-stakes question turned into a recommendation,
                scored options and an agreed next move.
              </p>
              <div className="grid gap-6 sm:grid-cols-3">
                {decisionSprintCards.map((c) => (
                  <SimpleCard key={c.name} {...c} rung={c.name} href="/decision-sprint" cta="See the Decision Sprint" />
                ))}
              </div>
            </div>
          ) : null}

          {/* ONE-OFFS */}
          <div className="mb-16">
            <h3 className="mb-8 font-serif text-2xl sm:text-3xl">One-offs</h3>
            <p className="mb-8 text-sm text-foreground/80">Read: <Link to="/insights/$slug" params={{ slug: "13-week-cash-flow-forecast" }} className="underline underline-offset-4">the 13-week cash flow forecast</Link>; Read: <Link to="/insights/$slug" params={{ slug: "revenue-leakage" }} className="underline underline-offset-4">revenue leakage</Link>; Read: <Link to="/insights/$slug" params={{ slug: "commission-and-bonus-structure-design" }} className="underline underline-offset-4">commission and bonus structure design</Link>.</p>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {oneOffCards.map((c) => (
                <SimpleCard key={c.name} {...c} rung={c.name} cta={oneOffCta(c.name)} />
              ))}
            </div>
          </div>

          {/* RETAINERS */}
          <div className="mb-16">
            <h3 className="font-serif text-2xl sm:text-3xl">Retainers</h3>
            <p className="mb-8 mt-3 max-w-4xl text-sm text-foreground/80">
              Retainers are advisory rhythms, not days in the business. The standard weekly rhythm is about three days of Ajility time across the month: a weekly working session plus analysis and support between sessions.
            </p>
            <div className="grid gap-6 sm:grid-cols-3">
              {retainerCards.map((c) => (
                <SimpleCard key={c.name} {...c} rung={c.name} href="/contact" cta="Scope my retainer" />
              ))}
            </div>
            <p className="mt-6 text-center text-sm text-foreground/80">{RENEW_OR_EXIT}.</p>
            <p className="mt-6 text-center text-sm text-foreground/80">
              Businesses above $15M should expect the weekly rhythm to scope{" "}
              {priceOf("Weekly rhythm base, businesses above $15M")}.
            </p>
            <p className="mt-3 text-center text-sm text-muted-foreground">
              Most composed retainers for businesses under $15M start {priceOf("Fortnightly rhythm base")}, depending on the mix and the size of the business.
            </p>
            <p className="mt-3 text-center text-sm text-muted-foreground">
              For the occasional larger or investor-backed business that needs an embedded Fractional COO or CFO, one to three days a week tied to milestones and board objectives, Ajility prices on proposal: days in the business, business size, on-site requirements and term.
            </p>
          </div>

          {/* CREDIT RULE */}
          <div className="mb-12 text-center">
            <p className="mx-auto max-w-3xl text-sm text-foreground/80">{`${CREDIT_RULE}.`}</p>
          </div>

          {/* FULL LIST */}
          <details className="group card-panel">
            <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between py-2 text-sm font-medium [&::-webkit-details-marker]:hidden">
              <span>The complete price list</span>
              <ChevronRight className="h-4 w-4 shrink-0 text-steel transition group-open:rotate-90" />
            </summary>
            <div
              tabIndex={0}
              role="region"
              aria-label="Ajility published pricing table"
              className="scroll-table mt-4 overflow-x-auto"
            >
              <table className="w-full min-w-[42rem] border-collapse text-left text-sm">
                <caption className="mb-4 text-left text-sm text-muted-foreground">
                  Ajility published pricing, all figures quoted plus GST
                </caption>
                <thead>
                  <tr className="border-b border-border">
                    <th scope="col" className="py-3 pr-4 text-xs font-semibold uppercase tracking-wider text-steel">Tier</th>
                    <th scope="col" className="py-3 pr-4 text-xs font-semibold uppercase tracking-wider text-steel">What you get</th>
                    <th scope="col" className="py-3 pr-4 text-xs font-semibold uppercase tracking-wider text-steel">How long</th>
                    <th scope="col" className="py-3 text-xs font-semibold uppercase tracking-wider text-steel">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.flatMap((tier) =>
                    tier.options.map((o) => (
                      <tr
                        key={`${tier.id}-${o.id}`}
                        className="border-b border-border align-top last:border-0 cursor-pointer hover:bg-mist/50"
                        onClick={() => trackEvent("price_table_click", { tier: tier.name, item_name: o.name })}
                      >
                        <td className="py-3 pr-4 text-foreground/70">{tier.name}</td>
                        <td className="py-3 pr-4">
                          <span className="font-medium text-midnight-deep">{o.name}</span>
                          <span className="mt-1 block text-foreground/70">{o.plainDescription}</span>
                        </td>
                        <td className="py-3 pr-4 text-foreground/70">{o.duration}</td>
                        <td className="py-3 text-foreground/80">{formatPrice(o.price) ?? "Scoped on a call"}</td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      </section>

      {/* QUALIFICATION / 90-DAY STANDARD */}
      <section className="bg-midnight-deep py-20 text-frost sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="mb-12 text-center">
            <span className="eyebrow on-dark mb-3 block">The 90-Day Standard</span>
            <h2 className="font-serif text-3xl sm:text-4xl">Who this works for</h2>
            <p className="mx-auto mt-4 max-w-3xl text-frost/70">
              {NINETY_DAY_STANDARD}. The standard is met when the business is genuinely ready
              to execute. Here is what ready looks like:
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {qualification.map((c) => (
              <div key={c.title} className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <h3 className="font-serif text-xl">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-frost/70">{c.desc}</p>
              </div>
            ))}
          </div>

          <p className="mt-12 text-center text-frost/80">
            If that sounds like your business, you are likely a strong fit. If you are not
            sure, the free call will tell us both quickly.
          </p>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl">How much does a fractional COO cost in Australia?</h2>
          <div className="mt-6 space-y-5 text-lg leading-relaxed text-charcoal">
            <p>
              Published Australian market guides put fractional COO retainers between $6,000 and $25,000 a month plus GST, with most engagements landing in the $8,000 to $16,000 range (Expert360's 2026 hiring guide; SEEK salary data 2025 as cited by Fractionus). A full-time COO in Australia costs $280,000 to $340,000 a year once superannuation, leave and on-costs are included.
            </p>
            <p>
              Ajility publishes its actual prices rather than a range. The Operating Diagnostic is {priceOf("Operating Diagnostic")}, the Full Diagnostic and 90-Day Roadmap {priceOf("Full Diagnostic and 90-Day Roadmap")}, and composed retainers run {priceOf("Fortnightly rhythm base")} for a fortnightly rhythm. Every price on this page is a from price based on the size of the business and how much of the work is on site.{" "}
              If the question behind the price is whether to hire at all, read <Link to="/insights/$slug" params={{ slug: "hire-a-general-manager-or-a-fractional-one" }} className="underline underline-offset-4">should I hire a general manager or a fractional one</Link> and <Link to="/insights/$slug" params={{ slug: "how-to-step-back-from-sales-in-your-own-business" }} className="underline underline-offset-4">how to step back from sales in your own business</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <div className="mb-12 text-center">
            <span className="eyebrow mb-3 block text-steel">FAQ</span>
            <h2 className="font-serif text-3xl sm:text-4xl">Common questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((f) => (
              <details key={f.q} className="group card-panel transition hover:border-steel/50">
                <summary className="flex min-h-11 cursor-pointer list-none flex-col justify-center -my-2 py-2 [&::-webkit-details-marker]:hidden">
                  <h3 className="flex items-center justify-between font-serif text-lg">
                    <span>{f.q}</span>
                    <ChevronRight className="h-5 w-5 shrink-0 text-steel transition group-open:rotate-90" />
                  </h3>
                </summary>
                <p className="mt-4 text-foreground/70">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-mist py-20 sm:py-28">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <span className="eyebrow mb-3 block text-steel">Get started</span>
          <h2 className="font-serif text-3xl sm:text-4xl">Not sure where to start? Start free.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-foreground/70">
            Take the free Operating Scan, or book a free 30-minute fit call. We will point you at the right starting point, even if it is not us.
          </p>
          <div className="mt-8 flex flex-col items-start justify-center gap-4 sm:flex-row">
            <Link
              to="/scan"
              className="inline-flex items-center gap-2 rounded-full bg-midnight px-6 py-3 text-sm font-medium text-frost transition hover:bg-midnight-deep"
            >
              Start the Operating Scan <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <BookFitCall
              placement="packages_footer"
              buttonClassName="inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-medium text-foreground transition hover:bg-mist"
              noteClassName="mt-2 text-sm font-medium text-charcoal/80 hover:text-midnight"
            >
              Book a fit call <Calendar className="h-4 w-4" aria-hidden="true" />
            </BookFitCall>
          </div>
        </div>
      </section>
    </>
  );
}
